import React, { useState, useEffect, useContext, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBookmark as solidBookmark, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faBookmark as regularBookmark } from '@fortawesome/free-regular-svg-icons';
import { WatchlistContext } from '../../context/WatchlistContext';
import { AuthContext } from '../../context/AuthContext';
import ErrorNotification from '../Error/ErrorNotification';

// In-memory cache to store watchlist check results
const watchlistCache = new Map();

// Create a debounce function to limit API calls
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

const Button = styled.button`
  background-color: ${props => props.$inWatchlist ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  width: 100%;
  font-size: 14px;
  font-weight: 600;
  box-shadow: ${props => props.$inWatchlist ? 
    '0 4px 15px rgba(59, 130, 246, 0.4)' : 
    '0 4px 15px rgba(0, 0, 0, 0.2)'};
  
  &:hover {
    background-color: ${props => props.$inWatchlist ? 'var(--primary-light)' : 'rgba(255, 255, 255, 0.2)'};
    transform: translateY(-3px) scale(1.02);
    box-shadow: ${props => props.$inWatchlist ? 
      '0 7px 20px rgba(59, 130, 246, 0.5)' : 
      '0 7px 20px rgba(0, 0, 0, 0.3)'};
  }
  
  &:active {
    transform: translateY(-1px) scale(1);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: wait;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    margin-right: 10px;
    font-size: 16px;
    color: ${props => props.$inWatchlist ? '#c4b5fd' : 'inherit'};
    transition: transform 0.3s ease;
    flex-shrink: 0;
    ${props => props.$isLoading && `
      animation: spin 1s linear infinite;
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}
  }
  
  @media (max-width: 768px) {
    padding: 8px 12px;
    font-size: 13px;
    
    svg {
      margin-right: 6px;
      font-size: 14px;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const WatchlistButton = ({ contentType, contentId }) => {
  const { isInWatchlist, addToWatchlist, removeFromWatchlist, loading: contextLoading, error: watchlistError, setError } = useContext(WatchlistContext);
  const { currentUser, openLoginModal } = useContext(AuthContext);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isInitialMount = useRef(true);
  const checkInProgress = useRef(false);

  // Generate a cache key based on content type, id and user
  const getCacheKey = () => {
    return `${contentType}-${contentId}-${currentUser?._id || 'guest'}`;
  };

  // Check if the result is in cache
  const checkCache = () => {
    const cacheKey = getCacheKey();
    if (watchlistCache.has(cacheKey)) {
      const cachedData = watchlistCache.get(cacheKey);
      // Check if cache is still valid (5 minutes)
      if (Date.now() - cachedData.timestamp < 5 * 60 * 1000) {
        return cachedData.value;
      }
      // Cache expired, remove it
      watchlistCache.delete(cacheKey);
    }
    return null;
  };

  // Update the cache with new result
  const updateCache = (value) => {
    const cacheKey = getCacheKey();
    watchlistCache.set(cacheKey, {
      value,
      timestamp: Date.now()
    });
  };

  // Create a debounced version of the watchlist check
  const debouncedCheckWatchlist = useRef(
    debounce(async () => {
      if (!currentUser || !contentId || checkInProgress.current) return;
      
      // Check cache first
      const cachedResult = checkCache();
      if (cachedResult !== null) {
        setInWatchlist(cachedResult);
        setIsLoading(false);
        return;
      }

      checkInProgress.current = true;
      setIsLoading(true);
      
      try {
        const result = await isInWatchlist(contentType, contentId);
        setInWatchlist(result);
        
        // Update cache with the result
        updateCache(result);
      } catch (err) {
        console.error('Error checking watchlist:', err);
      } finally {
        setIsLoading(false);
        checkInProgress.current = false;
      }
    }, 300)
  ).current;

  useEffect(() => {
    // Skip the initial API call on first render if we have a cached result
    if (isInitialMount.current) {
      isInitialMount.current = false;
      
      const cachedResult = checkCache();
      if (cachedResult !== null) {
        setInWatchlist(cachedResult);
        return;
      }
    }

    if (currentUser && contentId) {
      debouncedCheckWatchlist();
    } else {
      setInWatchlist(false);
    }
    
    // Cleanup function
    return () => {
      checkInProgress.current = false;
    };
  }, [currentUser, contentType, contentId]);

  const handleClick = async () => {
    if (!currentUser) {
      openLoginModal();
      return;
    }

    setIsLoading(true);
    try {
      if (inWatchlist) {
        await removeFromWatchlist(contentType, contentId);
        setInWatchlist(false);
        
        // Update cache
        updateCache(false);
      } else {
        await addToWatchlist(contentType, contentId);
        setInWatchlist(true);
        
        // Update cache
        updateCache(true);
      }
    } catch (err) {
      console.error('Watchlist operation failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    if (setError) {
      setError(null);
    }
  };

  return (
    <ButtonContainer>
      <Button 
        onClick={handleClick} 
        disabled={isLoading || contextLoading}
        $inWatchlist={inWatchlist}
        $isLoading={isLoading}
      >
        {isLoading ? (
          <FontAwesomeIcon icon={faSpinner} />
        ) : (
          <FontAwesomeIcon icon={inWatchlist ? solidBookmark : regularBookmark} />
        )}
        {inWatchlist ? 'Remove' : 'Watchlist'}
      </Button>
      
      {watchlistError && (
        <div style={{ marginTop: '10px' }}>
          <ErrorNotification message={watchlistError} onClose={clearError} />
        </div>
      )}
    </ButtonContainer>
  );
};

export default WatchlistButton;